if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        this.snapToActiveMedia();
      }

      // 初期表示（?variant= ディープリンク等）でアクティブメディアが先頭でない場合に、
      // カルーセルを instant でその位置へ合わせる。メディアは Admin 登録順のまま描画され
      // 先頭への並べ替えを行わないため（docs/pdp_media_gallery.md）、位置合わせはここが担う。
      snapToActiveMedia() {
        const activeMedia = this.elements.viewer.querySelector('.is-active[data-media-id]');
        if (!activeMedia) return;
        activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'instant' });
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${activeMedia.dataset.mediaId}"]`
        );
        const thumbnailList = this.elements.thumbnails.querySelector('[id^="Slider-"]');
        if (!activeThumbnail || !thumbnailList) return;
        thumbnailList.scrollTo({ left: activeThumbnail.offsetLeft, behavior: 'instant' });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      // viaVariantChange=true はバリアント切替起点の呼び出し（product-info.js）。
      // 旧実装（Dawn 標準）はこのとき対象メディアをカルーセル先頭へ prepend していたが、
      // 並べ替えを廃止し「Admin 登録順のまま、当該メディアの位置まで横スクロール」へ変更（2026-07-03）。
      // 各カラーの variant 画像の直後にそのカラーのサブカットが並ぶ登録順を前提に、
      // 切替と同時にサブカット群へスワイプ/クリックで届く導線を作る（docs/pdp_media_gallery.md）。
      setActiveMedia(mediaId, viaVariantChange) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          // バリアント変更に伴う画像自動切替ではページ縦スクロールを行わない。
          // 購入ボックスや商品説明を見ている最中にバリアントを選ぶたび、トップのメイン画像まで
          // 視点を強制的に戻される挙動を廃止するため。縦スクロールはサムネイル明示クリック
          // （viaVariantChange=false）時のみ許可し、そのとき選んだ画像を視界に入れる利便は維持する。
          if (viaVariantChange) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail, viaVariantChange);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail, anchorToStart = false) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        // anchorToStart（バリアント変更起点）では、可視位置にあっても必ず左端へ寄せる。
        // 当該サムネイルを起点に、右側へ同カラーのサブカットが続く視界を作るため。
        if (!anchorToStart && this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
